import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { cn } from "@/shared/lib/utils";
import type { FeatureFlag } from "./types";
import { FeatureFlagValueType as VT } from "./types";
import { FeatureFlagTypeBadge } from "./FeatureFlagTypeBadge";
import { useUpdateFeatureFlag } from "./use-update-feature-flag";
import { parseInputValue, valueToInputString, valuesEqual } from "./parse-value";

type View = "form" | "discard-confirm";

const REASON_MAX_LENGTH = 500;

interface EditFeatureFlagDialogProps {
  readonly flag: FeatureFlag | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function EditFeatureFlagDialog({
  flag,
  open,
  onOpenChange,
  onSuccess,
}: EditFeatureFlagDialogProps) {
  const { t } = useTranslation();
  const [rawValue, setRawValue] = useState("");
  const [initialRaw, setInitialRaw] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>("form");
  const update = useUpdateFeatureFlag();

  useEffect(() => {
    if (open && flag) {
      const initial = valueToInputString(flag.value, flag.valueType);
      setRawValue(initial);
      setInitialRaw(initial);
      setReason("");
      setError(null);
      setView("form");
    }
  }, [open, flag?.id, flag?.value, flag?.valueType, flag]);

  if (!flag) return null;

  const parsed = parseInputValue(rawValue, flag.valueType);
  const isValid = parsed.ok;
  const isDirty = rawValue !== initialRaw;
  const parsedValue = parsed.ok ? parsed.value : null;
  const isSameAsCurrent = parsed.ok && valuesEqual(parsedValue, flag.value);

  const handleOpenChange = (next: boolean) => {
    if (!next && update.isPending) return;
    if (!next && view === "discard-confirm") {
      setView("form");
      return;
    }
    if (!next && isDirty) {
      setView("discard-confirm");
      return;
    }
    onOpenChange(next);
  };

  const confirmDiscard = () => {
    setView("form");
    onOpenChange(false);
  };

  const handleSubmit = () => {
    if (!parsed.ok) return;
    if (isSameAsCurrent) return;
    setError(null);
    const trimmedReason = reason.trim();
    update.mutate(
      {
        id: flag.id,
        input: {
          value: parsed.value,
          ...(trimmedReason.length > 0 ? { reason: trimmedReason } : {}),
        },
      },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: (err) => {
          setError(
            resolveMutationErrorMessage(err, t, {
              forbiddenKey: "featureFlags.errors.forbidden",
              serverPrefixKey: "featureFlags.errors.serverPrefix",
              genericKey: "featureFlags.edit.error",
            }),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="edit-feature-flag-dialog">
        {view === "form" && (
          <div>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
                <span className="break-all font-mono text-base">{flag.id}</span>
                <FeatureFlagTypeBadge valueType={flag.valueType} />
              </DialogTitle>
              <DialogDescription>{t("featureFlags.edit.description")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-1.5">
                <label
                  htmlFor="edit-feature-flag-value"
                  className="font-sans text-sm font-medium text-foreground"
                >
                  {t("featureFlags.edit.valueLabel")}
                </label>
                <ValueInput
                  valueType={flag.valueType}
                  value={rawValue}
                  onChange={setRawValue}
                  disabled={update.isPending}
                />
                {!parsed.ok && rawValue.length > 0 && (
                  <p
                    data-testid="edit-feature-flag-parse-error"
                    className="font-sans text-xs font-medium text-destructive"
                  >
                    {t(parsed.errorKey)}
                  </p>
                )}
                {flag.valueType === VT.JSON && (
                  <p className="font-sans text-xs font-normal text-muted-foreground">
                    {t("featureFlags.edit.jsonHint")}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="edit-feature-flag-reason"
                  className="font-sans text-sm font-medium text-foreground"
                >
                  {t("featureFlags.edit.reasonLabel")}
                </label>
                <Textarea
                  id="edit-feature-flag-reason"
                  data-testid="edit-feature-flag-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("featureFlags.edit.reasonPlaceholder")}
                  maxLength={REASON_MAX_LENGTH}
                  disabled={update.isPending}
                  rows={3}
                />
                <p className="font-sans text-xs font-normal text-muted-foreground">
                  {t("featureFlags.edit.reasonHint", {
                    current: reason.length,
                    max: REASON_MAX_LENGTH,
                  })}
                </p>
              </div>

              {error && (
                <p
                  data-testid="edit-feature-flag-error"
                  className="font-sans text-sm font-medium text-destructive"
                >
                  {error}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={update.isPending}
              >
                {t("featureFlags.actions.cancel")}
              </Button>
              <Button
                data-testid="confirm-edit-feature-flag-button"
                onClick={handleSubmit}
                disabled={!isValid || !isDirty || isSameAsCurrent || update.isPending}
              >
                {update.isPending ? t("featureFlags.edit.saving") : t("featureFlags.actions.save")}
              </Button>
            </DialogFooter>
          </div>
        )}

        {view === "discard-confirm" && (
          <div data-testid="discard-feature-flag-edit-dialog" className="mx-auto max-w-md">
            <DialogHeader>
              <DialogTitle>{t("featureFlags.edit.discardTitle")}</DialogTitle>
              <DialogDescription>{t("featureFlags.edit.discardBody")}</DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setView("form")}>
                {t("featureFlags.edit.keepEditing")}
              </Button>
              <Button
                variant="destructive"
                data-testid="confirm-discard-feature-flag-edit-button"
                onClick={confirmDiscard}
              >
                {t("featureFlags.edit.discardConfirm")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ValueInputProps {
  readonly valueType: FeatureFlag["valueType"];
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly disabled: boolean;
}

function ValueInput({ valueType, value, onChange, disabled }: ValueInputProps) {
  const { t } = useTranslation();

  if (valueType === VT.BOOLEAN) {
    return (
      <div
        role="radiogroup"
        aria-label={t("featureFlags.edit.valueLabel")}
        className="inline-flex rounded-md border border-border p-1"
      >
        <BooleanOption
          label={t("featureFlags.edit.booleanTrue")}
          selected={value === "true"}
          onClick={() => onChange("true")}
          disabled={disabled}
          testId="edit-feature-flag-boolean-true"
        />
        <BooleanOption
          label={t("featureFlags.edit.booleanFalse")}
          selected={value === "false"}
          onClick={() => onChange("false")}
          disabled={disabled}
          testId="edit-feature-flag-boolean-false"
        />
      </div>
    );
  }

  if (valueType === VT.NUMBER) {
    return (
      <Input
        id="edit-feature-flag-value"
        data-testid="edit-feature-flag-number-input"
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    );
  }

  if (valueType === VT.STRING) {
    return (
      <Textarea
        id="edit-feature-flag-value"
        data-testid="edit-feature-flag-string-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={3}
      />
    );
  }

  return (
    <Textarea
      id="edit-feature-flag-value"
      data-testid="edit-feature-flag-json-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={6}
      spellCheck={false}
      className="font-mono text-xs"
    />
  );
}

interface BooleanOptionProps {
  readonly label: string;
  readonly selected: boolean;
  readonly onClick: () => void;
  readonly disabled: boolean;
  readonly testId: string;
}

function BooleanOption({ label, selected, onClick, disabled, testId }: BooleanOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-sm px-4 py-1.5 font-sans text-sm font-medium transition-colors",
        selected
          ? "bg-wander-orange text-white shadow-sm"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      {label}
    </button>
  );
}
